import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Users, ArrowRight, Sparkles, Heart } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const PROJECT_ROUTES = {
  1: '/em-construcao',   // Projeto Divas
  2: '/projeto-aguias',  // Projeto Águias
  3: '/em-construcao',   // Projeto Prospere
};

const ProjectsSection = () => {
  const { projectsData, theme } = useLanguage();
  const isDark = theme === 'dark';
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const bgY = useSpring(useTransform(scrollYProgress, [0, 1], ['-8%', '8%']), { stiffness: 50, damping: 18 });

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="py-24 md:py-36 relative overflow-hidden"
      style={{ background: isDark ? 'var(--bg-page)' : '#FAFAFA' }}
    >
      {/* Parallax decorative glow */}
      <motion.div style={{
        y: bgY, position: 'absolute', top: '-10%', right: '-5%',
        width: 500, height: 500,
        background: isDark
          ? 'radial-gradient(circle, rgba(184,134,11,0.07) 0%, transparent 70%)'
          : 'radial-gradient(circle, rgba(184,134,11,0.05) 0%, transparent 70%)',
        filter: 'blur(70px)', pointerEvents: 'none', zIndex: 0,
      }} />

      <div className="container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
            style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
          >
            <Heart size={14} style={{ color: 'var(--accent-gold-400)' }} />
            <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
              NOSSAS INICIATIVAS
            </span>
          </div>
          <h2 className="heading-hero mb-6">{projectsData.title}</h2>
          <p className="body-large mx-auto" style={{ color: 'var(--text-secondary)', maxWidth: '640px' }}>
            {projectsData.subtitle}
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projectsData.projects.map((project, i) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.13, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -10 }}
              className="relative group flex flex-col h-full"
            >
              {/* Hover glow border */}
              <div className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: 'linear-gradient(135deg, rgba(184,134,11,0.4), rgba(184,134,11,0.1))', zIndex: 0 }}
              />

              <Link
                to={PROJECT_ROUTES[project.id] || '/em-construcao'}
                style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', height: '100%' }}
              >
                <div
                  className="relative z-10 flex flex-col h-full p-8 rounded-2xl border transition-all duration-300"
                  style={{
                    background: isDark ? 'rgba(20,20,20,0.6)' : 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(12px)',
                    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    minHeight: 320,
                    cursor: 'pointer',
                  }}
                >
                  {/* Animated sparkle badge */}
                  <div className="flex items-center gap-2 mb-6">
                    <motion.div
                      animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.1, 1] }}
                      transition={{ repeat: Infinity, duration: 3 + i, ease: 'easeInOut' }}
                      style={{ color: 'var(--accent-gold-400)' }}
                    >
                      <Sparkles size={18} />
                    </motion.div>
                    <span className="mono-text" style={{ fontSize: '0.7rem', color: 'var(--accent-gold-400)', letterSpacing: '0.1em' }}>
                      {project.tagline}
                    </span>
                  </div>

                  <div className="flex-1">
                    <h3 className="font-serif font-bold text-2xl mb-4" style={{ color: 'var(--text-primary)', lineHeight: 1.2 }}>
                      {project.name}
                    </h3>
                    <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                      {project.description}
                    </p>

                    <div className="flex items-center gap-2 mt-auto">
                      <Users size={14} style={{ color: 'var(--accent-gold-400)', flexShrink: 0 }} />
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{project.audience}</span>
                    </div>
                  </div>

                  {/* Horizontal line */}
                  <div className="my-6 h-px" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />

                  <div className="flex items-center justify-between text-sm font-semibold">
                    <span style={{ color: 'var(--text-primary)' }}>Participe</span>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 group-hover:bg-gold group-hover:text-white"
                      style={{ background: isDark ? '#222' : '#F0EBE0', color: 'var(--text-muted)' }}
                    >
                      <ArrowRight size={14} />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
